<template>
  <div class="flex flex-col">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        Manage mobile app integration keys for admin units and services payment.
      </p>
      <div class="flex flex-wrap items-center gap-2 shrink-0">
        <button type="button" class="btn" :disabled="loading" @click="loadKeys">
          <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
          <span>Refresh</span>
        </button>
        <button
          v-if="loaded && keysConfigured"
          type="button"
          class="btn-sm-danger"
          :disabled="deleting || loading || saving"
          @click="confirmDelete"
        >
          <i v-if="deleting" class="fa fa-spinner fa-spin"></i>
          <i v-else class="fa-solid fa-trash"></i>
          <span>Delete keys</span>
        </button>
      </div>
    </div>

    <div class="relative min-h-[280px]">
      <div
        v-if="loading && !loaded"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded border border-gray-200 bg-white/90"
      >
        <i class="fa-solid fa-circle-notch fa-spin text-3xl text-primary"></i>
        <p class="mt-3 text-sm text-gray-500">Loading mobile app keys...</p>
      </div>

      <div :class="{ invisible: loading && !loaded }">
        <div class="mb-4">
          <span
            class="px-2 py-0.5 rounded-full text-xs font-semibold"
            :class="keysConfigured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'"
          >
            {{ keysConfigured ? "Keys configured" : "No keys configured" }}
          </span>
        </div>

        <form class="max-w-lg space-y-4" @submit.prevent="saveKeys">
          <div>
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Admin Units API Key
            </label>
            <div class="relative mt-1">
              <input
                v-model="form.adminUnitsApiKey"
                :type="showAdminUnitsKey ? 'text' : 'password'"
                autocomplete="off"
                placeholder="Enter admin units API key"
                class="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
                :disabled="loading || saving"
                required
              />
              <button
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
                :title="showAdminUnitsKey ? 'Hide key' : 'Show key'"
                @click="showAdminUnitsKey = !showAdminUnitsKey"
              >
                <i :class="showAdminUnitsKey ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
              </button>
            </div>
          </div>

          <div>
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Services Payment API Key
            </label>
            <div class="relative mt-1">
              <input
                v-model="form.servicesPaymentApiKey"
                :type="showServicesPaymentKey ? 'text' : 'password'"
                autocomplete="off"
                placeholder="Enter services payment API key"
                class="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
                :disabled="loading || saving"
                required
              />
              <button
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
                :title="showServicesPaymentKey ? 'Hide key' : 'Show key'"
                @click="showServicesPaymentKey = !showServicesPaymentKey"
              >
                <i :class="showServicesPaymentKey ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
              </button>
            </div>
          </div>

          <div class="flex flex-wrap gap-2 pt-1">
            <button type="submit" class="btn-primary" :disabled="loading || saving || deleting">
              <i v-if="saving" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-save"></i>
              <span>{{ keysConfigured ? "Update keys" : "Save keys" }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useConfirmDialog, useToast } from "@/composables";
import {
  deleteMobileAppKeys,
  extractErrorMessage,
  fetchMobileAppKeys,
  saveMobileAppKeys,
} from "../mobileAppKeysApi";

const { successToast, errorToast, warningToast } = useToast();
const { showConfirm } = useConfirmDialog();

const loading = ref(false);
const saving = ref(false);
const deleting = ref(false);
const loaded = ref(false);
const keysConfigured = ref(false);
const showAdminUnitsKey = ref(false);
const showServicesPaymentKey = ref(false);
const form = ref({
  adminUnitsApiKey: "",
  servicesPaymentApiKey: "",
});

const applyKeysToForm = (
  adminUnitsApiKey: string | null,
  servicesPaymentApiKey: string | null,
  configured: boolean
) => {
  keysConfigured.value = configured;
  form.value.adminUnitsApiKey = adminUnitsApiKey || "";
  form.value.servicesPaymentApiKey = servicesPaymentApiKey || "";
};

const loadKeys = async () => {
  loading.value = true;
  try {
    const keys = await fetchMobileAppKeys();
    applyKeysToForm(keys.adminUnitsApiKey, keys.servicesPaymentApiKey, keys.configured);
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } };
    if (err.response?.status === 404) {
      keysConfigured.value = false;
      form.value = { adminUnitsApiKey: "", servicesPaymentApiKey: "" };
    } else {
      errorToast("Error", extractErrorMessage(error, "Could not load mobile app keys"));
    }
  } finally {
    loading.value = false;
    loaded.value = true;
  }
};

const saveKeys = async () => {
  const adminUnitsApiKey = form.value.adminUnitsApiKey.trim();
  const servicesPaymentApiKey = form.value.servicesPaymentApiKey.trim();

  if (!adminUnitsApiKey) {
    warningToast("Validation", "Enter an admin units API key");
    return;
  }
  if (!servicesPaymentApiKey) {
    warningToast("Validation", "Enter a services payment API key");
    return;
  }

  saving.value = true;
  try {
    await saveMobileAppKeys({ adminUnitsApiKey, servicesPaymentApiKey });
    successToast("Success", "Mobile app keys saved");
    await loadKeys();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not save mobile app keys"));
  } finally {
    saving.value = false;
  }
};

const confirmDelete = async () => {
  const confirmed = await showConfirm(
    "Delete mobile app keys? This will remove the configured integration keys.",
    {
      title: "Delete Mobile App Keys",
      variant: "danger",
      okTitle: "Delete",
      cancelTitle: "Cancel",
    }
  );
  if (!confirmed) return;

  deleting.value = true;
  try {
    await deleteMobileAppKeys();
    successToast("Success", "Mobile app keys deleted");
    form.value = { adminUnitsApiKey: "", servicesPaymentApiKey: "" };
    keysConfigured.value = false;
    showAdminUnitsKey.value = false;
    showServicesPaymentKey.value = false;
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not delete mobile app keys"));
  } finally {
    deleting.value = false;
  }
};

onMounted(() => {
  loadKeys();
});

defineExpose({ refresh: loadKeys });
</script>
